import { useState } from 'react';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const COUNTRIES = [
  // Active
  { code: 'PL', name: 'Польша', flag: '🇵🇱', status: 'active', vat: 23, currency: 'PLN' },
  { code: 'ES', name: 'Hiszpania / España', flag: '🇪🇸', status: 'active', vat: 21, currency: 'EUR' },
  // Coming soon
  { code: 'US', name: 'США', flag: '🇺🇸', status: 'soon', vat: 0, currency: 'USD' },
  { code: 'PT', name: 'Португалия', flag: '🇵🇹', status: 'soon', vat: 23, currency: 'EUR' },
  { code: 'SK', name: 'Словакия', flag: '🇸🇰', status: 'soon', vat: 20, currency: 'EUR' },
  { code: 'HU', name: 'Венгрия', flag: '🇭🇺', status: 'soon', vat: 27, currency: 'HUF' },
  { code: 'LT', name: 'Литва', flag: '🇱🇹', status: 'soon', vat: 21, currency: 'EUR' },
  { code: 'LV', name: 'Латвия', flag: '🇱🇻', status: 'soon', vat: 21, currency: 'EUR' },
  { code: 'CZ', name: 'Чехия', flag: '🇨🇿', status: 'soon', vat: 21, currency: 'CZK' },
  { code: 'EE', name: 'Эстония', flag: '🇪🇪', status: 'soon', vat: 22, currency: 'EUR' },
  { code: 'CA', name: 'Канада', flag: '🇨🇦', status: 'soon', vat: 5, currency: 'CAD' },
  { code: 'AE', name: 'Дубай (ОАЭ)', flag: '🇦🇪', status: 'soon', vat: 5, currency: 'AED' },
  { code: 'GB', name: 'Великобритания', flag: '🇬🇧', status: 'soon', vat: 20, currency: 'GBP' },
];

export { COUNTRIES };

export default function CountrySelect() {
  const { user, setPage } = useApp();
  const { t } = useI18n();
  const [selectedCountry, setSelectedCountry] = useState(
    localStorage.getItem('selected_accounting_country') || localStorage.getItem('selected_country') || 'PL'
  );
  const [notifyEmail, setNotifyEmail] = useState('');
  const [subscribed, setSubscribed] = useState({});

  function selectCountry(code) {
    const country = COUNTRIES.find(c => c.code === code);
    if (country.status === 'active') {
      // Spain has its own taxpayer context; do not relabel Polish companies.
      localStorage.setItem('selected_accounting_country', code);
      if (code === 'PL') localStorage.setItem('selected_country', code);
      setSelectedCountry(code);
      setPage(code === 'ES' ? 'spain_accounting' : 'dashboard');
    }
  }

  function subscribeToCountry(code) {
    if (!notifyEmail) {
      alert('Введите email для уведомления');
      return;
    }
    setSubscribed({ ...subscribed, [code]: true });
    // In real app, save to backend
    localStorage.setItem(`notify_${code}`, notifyEmail);
  }

  const activeCountries = COUNTRIES.filter(c => c.status === 'active');
  const soonCountries = COUNTRIES.filter(c => c.status === 'soon');

  return (
    <div className="page">
      <BackButton />
      <h1>🌍 Выберите страну</h1>

      {/* Current Selection */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 24,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 48 }}>
          {COUNTRIES.find(c => c.code === selectedCountry)?.flag}
        </div>
        <div style={{ fontWeight: 'bold', fontSize: 18 }}>
          {COUNTRIES.find(c => c.code === selectedCountry)?.name}
        </div>
        <div style={{ opacity: 0.8, fontSize: 13 }}>
          Текущая страна
        </div>
      </div>

      {/* Active Countries */}
      <h3 style={{ marginBottom: 12 }}>✅ Доступные страны</h3>
      <div style={{ marginBottom: 24 }}>
        {activeCountries.map(country => (
          <div
            key={country.code}
            data-country={country.code}
            role="button"
            tabIndex={0}
            aria-pressed={selectedCountry === country.code}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectCountry(country.code); } }}
            onClick={() => selectCountry(country.code)}
            style={{
              background: selectedCountry === country.code 
                ? 'rgba(16,185,129,0.2)' 
                : 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: selectedCountry === country.code 
                ? '2px solid #10b981' 
                : '2px solid transparent',
            }}
          >
            <span style={{ fontSize: 32 }}>{country.flag}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold' }}>{country.name}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {country.code === 'ES' ? 'PGC / PGC PYMES · IVA (Península/Baleares) · EUR; IGIC/IPSI no disponibles' : `VAT: ${country.vat}% • ${country.currency}`}
              </div>
            </div>
            {selectedCountry === country.code && (
              <span style={{ color: '#10b981', fontSize: 20 }}>✓</span>
            )}
          </div>
        ))}
      </div>

      {/* Coming Soon Countries */}
      <h3 style={{ marginBottom: 12 }}>🔜 Скоро будет доступно</h3>
      
      {/* Email for notifications */}
      <div style={{
        background: 'rgba(99,102,241,0.1)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          📧 Оставьте email, чтобы узнать о запуске:
        </div>
        <input
          type="email"
          value={notifyEmail}
          onChange={(e) => setNotifyEmail(e.target.value)}
          placeholder="your@email.com"
          style={{
            width: '100%', padding: 12, borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.05)', color: '#fff',
          }}
        />
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: 8,
      }}>
        {soonCountries.map(country => (
          <div
            key={country.code}
            style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 12,
              opacity: 0.7,
              position: 'relative',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 24 }}>{country.flag}</span>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: 14 }}>{country.name}</div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>VAT: {country.vat}%</div>
              </div>
            </div>
            {subscribed[country.code] ? (
              <div style={{
                background: '#10b981',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: 10,
                textAlign: 'center',
              }}>
                ✓ Подписка оформлена
              </div>
            ) : (
              <button
                onClick={() => subscribeToCountry(country.code)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'rgba(99,102,241,0.3)',
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                🔔 Уведомить
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Info */}
      <div style={{
        background: 'rgba(245,158,11,0.1)',
        borderRadius: 12,
        padding: 16,
        marginTop: 24,
        fontSize: 13,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>💡 Мульти-страновая поддержка</div>
        <ul style={{ paddingLeft: 20, margin: 0, opacity: 0.8 }}>
          <li>Каждая страна = свои налоговые ставки</li>
          <li>Локальные валюты и банковские переводы</li>
          <li>Шаблоны документов под законодательство</li>
          <li>Местные праздники и сроки подачи</li>
        </ul>
      </div>
    </div>
  );
}
